import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Helmet } from 'react-helmet';
import { toast } from 'react-hot-toast';

import Modal from './Modal';

import { AuthContext } from '../contexts/AuthContext';
import DataTable from 'react-data-table-component';
import LabourerView from './LabourerView';
import LabourerAllocationForm from './LabourerAllocationForm';
import AddLabourerForm from './AddLabourerForm';
import UpdateLabourerForm from './UpdateLabourerForm';
import ConfirmPrompt from './ConfirmPrompt';
import TableLoader from './TableLoader';
import UserProfilePhoto from './UserProfilePhoto';
import SectionHeader from './SectionHeader';

import labourerService from '../services/labourerService';

import useLabourers from '../hooks/useLabourers';
import useLabourer from '../hooks/useLabourer';
import useDebounce from '../hooks/useDebounce';

import { formatDateToSydneyTimeZone } from '../utils/dateUtils';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Labour = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [selectedLabourer, setSelectedLabourer] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);

  useEffect(() => { setPage(1); }, [search]);

  const { data, isLoading, mutate } = useLabourers({
    page,
    perPage,
    sortField,
    sortOrder,
    search
  });

  const labourers = data?.labourers;
  const labourerCount = data?.count;

  const [isViewLabourerOpen, setIsViewLabourerOpen] = useState(false);
  const [isAllocateLabourerOpen, setIsAllocateLabourerOpen] = useState(false);
  const [isAddLabourerModalOpen, setIsAddLabourerModalOpen] = useState(false);
  const [isUpdateLabourerModalOpen, setIsUpdateLabourerModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);

  const [isUpdatingLabourer, setIsUpdatingLabourer] = useState(false);
  const [isInvitingLabourer, setIsInvitingLabourer] = useState(false);
  const [isDeletingLabourer, setIsDeletingLabourer] = useState(false);

  const deepLinkedUserId = location.state?.labourerUserId || searchParams.get('labourerUserId');

  const { data: deepLinkedData } = useLabourer({
    userId: deepLinkedUserId,
    shouldFetch: !!deepLinkedUserId
  });

  useEffect(() => {
    if (!deepLinkedData?.labourer) return;

    setSelectedLabourer(deepLinkedData.labourer);
    setIsViewLabourerOpen(true);

    navigate(location.pathname, { replace: true });
  }, [deepLinkedData]);

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const onRowClicked = (row) => {
    setSelectedLabourer(row);
    setIsViewLabourerOpen(true);
  }

  const handleAllocate = (row) => {
    setSelectedLabourer(row);
    setIsAllocateLabourerOpen(true);
  };

  const handleAllocationCompleted = async () => {
    setIsAllocateLabourerOpen(false);
    mutate();
  }

  const handleUpdateLabourerClicked = (row) => {
    setIsUpdateLabourerModalOpen(true);
  }

  const handleUpdateLabourer = async (updateData) => {
    try {
      setIsUpdatingLabourer(true);

      const { labourer } = await labourerService.updateLabourer({
        labourerId: selectedLabourer._id,
        updateData
      });

      await mutate();

      setIsUpdateLabourerModalOpen(false);
      setIsViewLabourerOpen(false);
      setSelectedLabourer(null);

      toast.success(`${labourer?.user?.fullName} updated`, { icon: '🚀' });
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while updating labourer');
    } finally {
      setIsUpdatingLabourer(false);
    }
  }

  const handleDeleteClicked = (labourer) => {
    setSelectedLabourer(labourer);
    setIsConfirmDeleteModalOpen(true);
  }

  const deleteLabourer = async (labourer) => {
    try {
      setIsDeletingLabourer(true);

      await labourerService.deleteLabourer(labourer._id);
      await mutate();

      setIsViewLabourerOpen(false);
      setSelectedLabourer(null);

      toast.success(`${labourer?.user?.fullName} deleted`, { icon: '🚀' });
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while deleting labourer');
    } finally {
      setIsConfirmDeleteModalOpen(false);
      setIsDeletingLabourer(false);
    }
  }

  const handleSort = async (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const columns = [
    {
      cell: row => <UserProfilePhoto photoUrl={row.user.profilePicture} fullName={row.user.fullName} />,
      width: '75px'
    },
    {
      name: 'Name',
      selector: row => row.user.fullName,
      sortable: true,
      sortField: 'user.fullName'
    },
    {
      name: 'Email',
      selector: row => row.user.email,
      sortable: true,
      sortField: 'user.email'
    },
    {
      name: 'Date of Birth',
      selector: row => row.user.dateOfBirth ? formatDateToSydneyTimeZone(row.user.dateOfBirth) : 'N/A',
      sortable: true,
      sortField: 'user.dateOfBirth'
    },
    {
      name: 'Phone',
      selector: row => row.user.phoneNumber || 'N/A',
      sortable: true,
      sortField: 'user.phoneNumber'
    },
    {
      right: "true",
      cell: row => (
        <div className='flex items-center gap-2'>
          <button
            onClick={() => handleAllocate(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white text-nowrap'
          >
            Allocate
          </button>

          <button
            onClick={() => handleDeleteClicked(row)}
            className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white  text-nowrap'
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const handleInviteLabourer = async (data) => {
    try {
      setIsInvitingLabourer(true);

      const { labourer } = await labourerService.inviteLabourer(data);
      await mutate();

      setIsAddLabourerModalOpen(false);

      toast.success(`${labourer.fullName} invited`, { icon: '🚀' });
    } catch (error) {
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while inviting labourer');
    } finally {
      setIsInvitingLabourer(false);
    }
  }

  if (!user) return;

  return (
    <>
      <Helmet>
        <title>Labourers | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Labourers'
          actionsLeft={
            <button
              onClick={() => setIsAddLabourerModalOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>}
          actionsRight={
            <div className="relative flex items-center">
              <MagnifyingGlassIcon className="absolute left-[10px] h-3 w-3 text-gray-600 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="!pl-[28px] pr-2 text-xs !h-[25px] !border border-none rounded-md bg-white text-gray-700 placeholder-gray-400 focus:outline-none w-48"
              />
            </div>
          }
        />

        <DataTable
          columns={columns}
          data={labourers}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={onRowClicked}
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={labourerCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </div>

      <Modal isOpen={isAddLabourerModalOpen} overflowType='visible'>
        <AddLabourerForm
          isLoading={isInvitingLabourer}
          onSubmit={handleInviteLabourer}
          onCloseClicked={() => setIsAddLabourerModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isUpdateLabourerModalOpen} overflowType='visible'>
        <UpdateLabourerForm
          labourer={selectedLabourer}
          isLoading={isUpdatingLabourer}
          onSubmit={handleUpdateLabourer}
          onCloseClicked={() => setIsUpdateLabourerModalOpen(false)}
        />
      </Modal>

      <Modal isOpen={isViewLabourerOpen}>
        <LabourerView
          labourerUserId={selectedLabourer?.user?._id}
          onAllocateClicked={() => setIsAllocateLabourerOpen(true)}
          onUpdateClicked={() => handleUpdateLabourerClicked(true)}
          onDeleteClicked={() => setIsConfirmDeleteModalOpen(true)}
          onCloseClicked={() => setIsViewLabourerOpen(false)}
        />
      </Modal>

      <Modal isOpen={isAllocateLabourerOpen}>
        <LabourerAllocationForm
          labourer={selectedLabourer}
          onAllocate={handleAllocationCompleted}
          onCloseClicked={() => setIsAllocateLabourerOpen(false)}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete ${selectedLabourer?.user?.fullName}?`}
          onConfirm={() => deleteLabourer(selectedLabourer)}
          onCancel={() => setIsConfirmDeleteModalOpen(null)}
          isLoading={isDeletingLabourer}
        />
      </Modal>
    </>
  )
}

export default Labour;
